import { useCallback, useMemo, useState } from 'react';
import { useT } from '@dia/i18n';
import {
  clearMarketingOptIn,
  describeBeds,
  listBedTypes,
  listHouses,
  listMarketingList,
  listRoomOccupancy,
  listRooms,
  marketingCsv,
  moveClaim,
  recordMarketingOptIn,
  releaseClaim,
  setRoomCapacity,
  type Guest,
  type MarketingListRow,
  type Room,
  type RoomOccupancyRow,
} from '@dia/db';
import { Button, StatusPill } from '@dia/ui';
import { supabase } from '../supabase';
import { Card, Field, Notice, Page, Pill, inputClass } from './lib/ui';
import { RequireAuth, type Identity } from './lib/identity';
import { loadOpenRequests, useLoad } from './lib/data';
import { formatDay, panamaDateOf } from './lib/time';
import { buildGrid, gridSummary, islandSleeps, type RoomCard } from './lib/rooms';

/**
 * Habitaciones: who sleeps in each room, and what that means for the work.
 *
 * Crew read it. The names come from the room_occupancy view (migration 016),
 * which carries a first name and a last name beside a room number and nothing
 * else: no email, no phone, no handle, no marketing field, no notes.
 *
 * Hosts and owners can also move somebody, which is the override a host needs
 * at the dock when a family rearranges itself. Owners additionally see the
 * marketing list and its CSV.
 *
 * The grid reads from the phone's cache, so it opens with no signal. Moving
 * somebody does not: first come first served cannot be queued.
 */
export function Habitaciones() {
  return <RequireAuth>{(who) => <Grid who={who} />}</RequireAuth>;
}

function Grid({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const boss = who.role !== 'crew';
  const owner = who.role === 'owner';

  const today = panamaDateOf();

  const load = useCallback(
    async () =>
      Promise.all([
        listRooms(supabase),
        listHouses(supabase),
        listRoomOccupancy(supabase),
        listBedTypes(supabase),
        loadOpenRequests(),
      ]),
    [],
  );
  const { data, reload } = useLoad(load, 'habitaciones', 60_000);

  const [rooms, houses, occupancy, bedTypes, requests] = data ?? [[], [], [], [], []];

  const grid = useMemo(
    () => buildGrid({ rooms, houses, occupancy, requests, today }),
    [rooms, houses, occupancy, requests, today],
  );
  const summary = useMemo(() => gridSummary(grid), [grid]);

  const [moving, setMoving] = useState<RoomOccupancyRow | null>(null);
  const [note, setNote] = useState<'saved' | 'failed' | 'signal' | null>(null);

  const roomName = (id: string) => {
    const room = rooms.find((r) => r.id === id);
    if (!room) return id;
    return lang === 'en' ? room.name_en : room.name_es;
  };

  async function act(job: () => Promise<unknown>) {
    setNote(null);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setNote('signal');
      return;
    }
    try {
      await job();
      setNote('saved');
      setMoving(null);
      await reload();
    } catch {
      setNote('failed');
    }
  }

  return (
    <Page title={t('ops.rooms.title')} back="/">
      <p className="text-inkMuted">{t('ops.rooms.lede')}</p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Pill tone="info">
          {t('ops.rooms.state.occupied')} {summary.occupied}
        </Pill>
        <Pill tone="warn">
          {t('ops.rooms.state.turnover')} {summary.turnover}
        </Pill>
        <Pill tone="neutral">
          {t('ops.rooms.state.empty')} {summary.empty}
        </Pill>
        <Pill tone="neutral">{t('ops.rooms.edit_total', { count: islandSleeps(rooms) })}</Pill>
      </div>

      {note === 'signal' && (
        <div className="mt-3">
          <Notice tone="warn">{t('ops.rooms.needs_signal')}</Notice>
        </div>
      )}
      {note === 'failed' && (
        <div className="mt-3">
          <Notice tone="danger">{t('ops.rooms.failed')}</Notice>
        </div>
      )}
      {note === 'saved' && (
        <div className="mt-3">
          <Notice tone="ok">{t('ops.rooms.moved')}</Notice>
        </div>
      )}

      {grid.length === 0 && <p className="mt-6">{t('ops.rooms.no_stay')}</p>}

      {grid.map((group) => (
        <section key={group.house.id} className="mt-6 flex flex-col gap-3">
          <h2 className="text-[20px] font-semibold">
            {lang === 'en' ? group.house.name_en : group.house.name_es}
          </h2>

          {group.cards.map((card) => (
            <RoomBlock
              key={card.room.id}
              card={card}
              bedLines={describeBeds(card.room.beds, bedTypes, lang)}
              boss={boss}
              onMove={setMoving}
              onRelease={(row) => void act(() => releaseClaim(supabase, row.id))}
            />
          ))}
        </section>
      ))}

      {moving && (
        <Card className="mt-6 flex flex-col gap-3">
          <h2 className="text-[20px] font-semibold">
            {moving.first_name} {moving.last_name}
          </h2>
          {rooms
            .filter((r) => r.claimable && r.id !== moving.room_id)
            .sort((a, b) => a.sort - b.sort)
            .map((room) => (
              <Button
                key={room.id}
                block
                variant="secondary"
                onClick={() =>
                  void act(() =>
                    moveClaim(supabase, moving.id, room.id, {
                      claimedBy: 'staff',
                      claimedByStaffId: who.staffId,
                    }),
                  )
                }
              >
                {t('ops.rooms.move_to', { room: roomName(room.id) })}
              </Button>
            ))}
          <Button variant="quiet" onClick={() => setMoving(null)}>
            {t('action.cancel')}
          </Button>
        </Card>
      )}

      {owner && <RoomEditor rooms={rooms} onSaved={reload} />}
      {owner && <MarketingList who={who} />}
      {boss && !owner && (
        <p className="mt-8 text-inkMuted">{t('ops.rooms.export_owner_only')}</p>
      )}
    </Page>
  );
}

/**
 * The capacity numbers, editable by an owner.
 *
 * They are assumptions until somebody who has stood in the room says otherwise
 * (see SESSIONS.md): which bedrooms are the four matching ones downstairs, and
 * how many people a futon really sleeps. Rather than leave that in a migration
 * nobody can reach, the two halves of the number are edited here. max_sleeps is
 * computed by the database, so the ceiling can never disagree with the beds.
 */
function RoomEditor({ rooms, onSaved }: { rooms: Room[]; onSaved: () => void }) {
  const { t, lang } = useT();
  const [note, setNote] = useState<'saved' | 'failed' | 'signal' | null>(null);
  const [draft, setDraft] = useState<Record<string, { base: string; extra: string }>>({});

  const bedrooms = rooms.filter((r) => r.claimable).sort((a, b) => a.sort - b.sort);

  const valueOf = (room: Room) =>
    draft[room.id] ?? { base: String(room.base_sleeps), extra: String(room.extra_sleeps) };

  async function save(room: Room) {
    setNote(null);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setNote('signal');
      return;
    }
    const value = valueOf(room);
    try {
      await setRoomCapacity(supabase, room.id, Number(value.base), Number(value.extra));
    } catch {
      setNote('failed');
      return;
    }
    setNote('saved');
    onSaved();
  }

  return (
    <section className="mt-10 flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-[20px] font-semibold">{t('ops.rooms.edit_title')}</h2>
      <p className="text-inkMuted">{t('ops.rooms.edit_lede')}</p>

      {note === 'signal' && <Notice tone="warn">{t('ops.rooms.needs_signal')}</Notice>}
      {note === 'failed' && <Notice tone="danger">{t('ops.rooms.failed')}</Notice>}
      {note === 'saved' && <Notice tone="ok">{t('ops.rooms.edit_saved')}</Notice>}

      {bedrooms.map((room) => {
        const value = valueOf(room);
        return (
          <Card key={room.id} className="flex flex-col gap-2">
            <h3 className="text-[20px] font-semibold">
              {lang === 'en' ? room.name_en : room.name_es}
            </h3>
            <Field label={t('ops.rooms.edit_base')}>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className={inputClass}
                value={value.base}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [room.id]: { ...value, base: e.target.value } }))
                }
              />
            </Field>
            <Field label={t('ops.rooms.edit_extra')}>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className={inputClass}
                value={value.extra}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [room.id]: { ...value, extra: e.target.value } }))
                }
              />
            </Field>
            <Button block variant="secondary" onClick={() => void save(room)}>
              {t('ops.rooms.edit_save')}
            </Button>
          </Card>
        );
      })}
    </section>
  );
}

function RoomBlock({
  card,
  bedLines,
  boss,
  onMove,
  onRelease,
}: {
  card: RoomCard;
  bedLines: Array<{ label: string; count: number; extra: boolean }>;
  boss: boolean;
  onMove: (row: RoomOccupancyRow) => void;
  onRelease: (row: RoomOccupancyRow) => void;
}) {
  const { t, lang } = useT();
  const tone = card.state === 'occupied' ? 'info' : card.state === 'turnover' ? 'warn' : 'neutral';

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[20px] font-semibold">
          {lang === 'en' ? card.room.name_en : card.room.name_es}
        </h3>
        <div className="flex flex-wrap gap-2">
          <Pill tone={tone}>{t(`ops.rooms.state.${card.state}`)}</Pill>
          {card.serviceDue && <Pill tone="danger">{t('ops.rooms.state.service')}</Pill>}
        </div>
      </div>

      <p className="text-inkMuted">
        {t('ops.rooms.people', { count: card.occupants.length, max: card.room.max_sleeps })}
        {card.room.en_suite && <span> . {t('ops.rooms.en_suite')}</span>}
      </p>

      <p className="text-inkMuted">
        <span className="font-medium">{t('ops.rooms.beds')}: </span>
        {bedLines.map((bed) => `${bed.count} x ${bed.label}`).join(', ') ||
          t('ops.rooms.photo_missing')}
      </p>

      {card.futonInUse && <Notice tone="warn">{t('ops.rooms.futon_open')}</Notice>}

      {card.occupants.length === 0 ? (
        <p className="text-inkMuted">{t('ops.rooms.nobody')}</p>
      ) : (
        <ul>
          {card.occupants.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-2 last:border-b-0"
            >
              <span>
                {row.first_name} {row.last_name}
                {row.is_lead && <span className="text-inkMuted"> ({t('ops.rooms.booker')})</span>}
              </span>
              {boss && (
                <span className="flex gap-2">
                  <Button variant="secondary" onClick={() => onMove(row)}>
                    {t('ops.rooms.move')}
                  </Button>
                  <Button variant="quiet" onClick={() => onRelease(row)}>
                    {t('ops.rooms.release')}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* The marketing list, owners only                                     */
/* ------------------------------------------------------------------ */

/**
 * Recording an opt in, and exporting the list.
 *
 * Nothing a guest types puts them on this list. A guest may leave an email and
 * a social handle in the guest app, and the wording there says plainly that it
 * signs them up for nothing. A person joins the list here, when a staff member
 * has asked them face to face at the property and they said yes. The database
 * refuses a guest who tries to stamp it for themselves.
 */
function MarketingList({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const [note, setNote] = useState<'saved' | 'failed' | 'signal' | null>(null);

  const load = useCallback(
    async () =>
      Promise.all([
        listMarketingList(supabase),
        supabase
          .from('guests')
          .select('*')
          .order('last_name')
          .then(({ data }) => (data ?? []) as Guest[]),
      ]),
    [],
  );
  const { data, reload } = useLoad(load, 'marketing', 0);
  const [list, guests] = data ?? [[] as MarketingListRow[], [] as Guest[]];

  async function act(job: () => Promise<unknown>) {
    setNote(null);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setNote('signal');
      return;
    }
    try {
      await job();
      setNote('saved');
      await reload();
    } catch {
      setNote('failed');
    }
  }

  function download() {
    const blob = new Blob([marketingCsv(list)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dia-bonito-marketing-${panamaDateOf()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="mt-10 flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-[20px] font-semibold">{t('ops.rooms.optin_title')}</h2>
      <p className="text-inkMuted">{t('ops.rooms.optin_lede')}</p>

      {note === 'signal' && <Notice tone="warn">{t('ops.rooms.needs_signal')}</Notice>}
      {note === 'failed' && <Notice tone="danger">{t('ops.rooms.failed')}</Notice>}
      {note === 'saved' && <Notice tone="ok">{t('ops.rooms.optin_saved')}</Notice>}

      <ul>
        {guests.map((guest) => (
          <li key={guest.id} className="flex flex-col gap-2 border-b border-line py-3 last:border-b-0">
            <span className="font-medium">
              {guest.first_name} {guest.last_name}
            </span>
            {guest.email || guest.social_handle ? (
              <span className="text-inkMuted">
                {[guest.email, guest.social_handle].filter(Boolean).join(' . ')}
              </span>
            ) : (
              <span className="text-inkMuted">{t('ops.rooms.optin_no_email')}</span>
            )}

            {guest.marketing_opt_in_at ? (
              <span className="flex flex-wrap items-center gap-2">
                <StatusPill tone="ok">
                  {t('ops.rooms.optin_done', {
                    date: formatDay(guest.marketing_opt_in_at.slice(0, 10), lang),
                  })}
                </StatusPill>
                <Button
                  variant="quiet"
                  onClick={() => void act(() => clearMarketingOptIn(supabase, guest.id))}
                >
                  {t('ops.rooms.optin_undo')}
                </Button>
              </span>
            ) : (
              <Button
                variant="secondary"
                onClick={() => void act(() => recordMarketingOptIn(supabase, guest.id, who.staffId))}
              >
                {t('ops.rooms.optin_record')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      {list.length === 0 ? (
        <p className="text-inkMuted">{t('ops.rooms.export_empty')}</p>
      ) : (
        <>
          <p className="text-inkMuted">{t('ops.rooms.export_count', { count: list.length })}</p>
          <Button onClick={download}>{t('ops.rooms.export')}</Button>
        </>
      )}
    </section>
  );
}
